import { useState } from 'react';
import { usePOS } from '@/contexts/POSContext';
import { useBCHPrice } from '@/hooks/useBCHPrice';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useDailySummary } from '@/hooks/useDailySummary';
import { useMerchantHeartbeat } from '@/hooks/useMerchantHeartbeat';
import { useTheme } from '@/hooks/useTheme';
import { InstallBanner } from '@/pages/SplashPage';
import { CheckoutView } from './CheckoutView';
import { DiscountView } from './DiscountView';
import { TipView } from './TipView';
import { PaymentView } from './PaymentView';
import { SuccessView } from './SuccessView';
import { HistoryView } from './HistoryView';
import { SettingsView } from './SettingsView';
import { MenuManageView } from './MenuManageView';
import { UpgradeView } from './UpgradeView';
import { InvoiceView } from './InvoiceView';
import { DashboardView } from './DashboardView';
import { CashStampsView } from './CashStampsView';
import { cn } from '@/lib/utils';

type NavTab = 'checkout' | 'menu-manage' | 'invoices' | 'cashstamps' | 'history' | 'dashboard' | 'settings';

const NAV_ITEMS: { id: NavTab; label: string; icon: string }[] = [
  { id: 'checkout',    label: 'Checkout', icon: '💳' },
  { id: 'menu-manage', label: 'Menu',     icon: '🛍️' },
  { id: 'invoices',    label: 'Invoice',  icon: '✉️' },
  { id: 'cashstamps',  label: 'Stamps',   icon: '🎁' },
  { id: 'dashboard',   label: 'Stats',    icon: '📊' },
  { id: 'history',     label: 'History',  icon: '📋' },
  { id: 'settings',    label: 'Settings', icon: '⚙️' },
];

function BCHPricePill({ currency }: { currency: string }) {
  const { data, isLoading } = useBCHPrice(currency);
  const isOnline = useOnlineStatus();

  if (isLoading && !data) {
    return (
      <div className="bg-zinc-100 dark:bg-zinc-800 rounded-full px-3 py-1 text-xs text-zinc-400 animate-pulse">
        BCH...
      </div>
    );
  }
  if (!data) return null;

  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(data.price);

  const isStale = data.stale || !isOnline;

  return (
    <div className={cn(
      'rounded-full px-3 py-1 text-xs font-semibold shrink-0 border flex items-center gap-1',
      isStale
        ? 'bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-800/50'
        : 'bg-[#0AC18E]/10 dark:bg-[#0AC18E]/20 text-[#0AC18E] border-[#0AC18E]/20'
    )}>
      {isStale && <span className="text-[9px]">~</span>}
      {formatted}
    </div>
  );
}

export function POSShell() {
  const { view, setView, settings, products } = usePOS();
  const { theme, setTheme } = useTheme();
  const isDark = theme === 'dark';
  const isOnline = useOnlineStatus();
  useDailySummary(settings);
  useMerchantHeartbeat();

  // First-run: show setup prompt if no BCH address has ever been saved.
  // Dismissed flag persists in localStorage so it only shows once.
  const [setupDismissed, setSetupDismissed] = useState(
    () => localStorage.getItem('bch-pos:setup-dismissed') === '1'
  );
  const showSetupBanner = !settings.bchAddress && !setupDismissed
    && view !== 'settings' && view !== 'payment' && view !== 'success' && view !== 'upgrade';

  const handleSetupDismiss = () => {
    localStorage.setItem('bch-pos:setup-dismissed', '1');
    setSetupDismissed(true);
  };

  const activeTab: NavTab =
    view === 'discount' || view === 'tip' || view === 'payment' || view === 'success' || view === 'upgrade'
      ? 'checkout'
      : view === 'dashboard'
      ? 'dashboard'
      : (view as NavTab);

  const subtitles: Record<string, string> = {
    checkout:     'Enter amount to charge',
    discount:     'Apply discount or coupon',
    tip:          'Add a tip',
    payment:      'Awaiting payment...',
    success:      'Payment received!',
    invoices:     'Create & share payment links',
    dashboard:    'Sales analytics & insights',
    history:      'Recent transactions',
    settings:     'Configure your terminal',
    'menu-manage':'Manage your products',
    cashstamps:   'Promotional CashStamps',
    upgrade:      'Unlock all features',
  };

  const totalProducts = products.length;

  return (
    <div className="flex flex-col h-[100dvh] bg-zinc-50 dark:bg-zinc-950">
      {/* Header */}
      <header className="shrink-0 bg-white dark:bg-zinc-900 border-b border-zinc-100 dark:border-zinc-800 px-4 py-3 flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl overflow-hidden shrink-0 flex items-center justify-center bg-[#0AC18E] shadow-sm">
          {settings.logoDataUrl ? (
            <img
              src={settings.logoDataUrl}
              alt={settings.merchantName || 'Logo'}
              className="w-full h-full object-contain"
            />
          ) : (
            <span className="text-white font-black text-sm">BCH</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-base font-bold text-zinc-900 dark:text-zinc-50 truncate">
            {settings.merchantName || 'BCHPAY.Cash'}
          </h1>
          <p className={cn(
            'text-xs font-medium',
            view === 'payment' || view === 'success' ? 'text-[#0AC18E]' : 'text-zinc-400'
          )}>
            {subtitles[view] ?? ''}
          </p>
        </div>
        {/* Quick dark mode toggle */}
        <button
          onClick={() => setTheme(isDark ? 'light' : 'dark')}
          className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-zinc-100 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 text-sm transition-colors hover:bg-zinc-200 dark:hover:bg-zinc-700"
          aria-label="Toggle dark mode"
        >
          {isDark ? '☀️' : '🌙'}
        </button>
        {(view === 'checkout' || view === 'payment') && (
          <BCHPricePill currency={settings.currency} />
        )}
      </header>

      {/* Install banner — shown only when not installed */}
      <InstallBanner />

      {/* First-run setup banner — shown until BCH address is configured */}
      {showSetupBanner && (
        <div className="mx-4 mt-3 bg-[#0AC18E] rounded-2xl px-4 py-3.5 flex items-center gap-3 shrink-0 shadow-lg shadow-[#0AC18E]/30">
          <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center shrink-0 text-xl">
            👋
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-white leading-snug">Welcome! One step to get started</p>
            <p className="text-[11px] text-white/80 leading-snug mt-0.5">Add your BCH address in Settings to start accepting payments.</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => setView('settings')}
              className="px-3 py-1.5 rounded-xl bg-white text-[#0AC18E] text-xs font-black hover:bg-white/90 active:scale-95 transition-all"
            >
              Set up →
            </button>
            <button
              onClick={handleSetupDismiss}
              className="text-white/60 hover:text-white text-lg leading-none transition-colors"
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Offline banner */}
      {!isOnline && (
        <div className="mx-4 mt-3 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/50 rounded-2xl px-4 py-2.5 flex items-center gap-2.5 shrink-0">
          <span className="text-amber-500 text-base shrink-0">📡</span>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-amber-700 dark:text-amber-400 leading-snug">You're offline</p>
            <p className="text-[11px] text-amber-600/80 dark:text-amber-500/80 leading-snug">BCH price may be outdated. Payment detection paused.</p>
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 overflow-y-auto px-4 pt-4 pb-2 min-h-0">
        {view === 'checkout'     && <CheckoutView />}
        {view === 'discount'     && <DiscountView />}
        {view === 'tip'          && <TipView />}
        {view === 'payment'      && <PaymentView />}
        {view === 'success'      && <SuccessView />}
        {view === 'history'      && <HistoryView />}
        {view === 'dashboard'    && <DashboardView />}
        {view === 'settings'     && <SettingsView />}
        {view === 'menu-manage'  && <MenuManageView />}
        {view === 'invoices'     && <InvoiceView />}
        {view === 'cashstamps'   && <CashStampsView />}
        {view === 'upgrade'      && <UpgradeView />}
      </main>

      {/* Bottom nav (hidden during payment / success / upgrade) */}
      {view !== 'discount' && view !== 'tip' && view !== 'payment' && view !== 'success' && view !== 'upgrade' && (
        <nav className="shrink-0 bg-white dark:bg-zinc-900 border-t border-zinc-100 dark:border-zinc-800 px-2 pb-safe">
          <div className="flex">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                onClick={() => setView(item.id)}
                className={cn(
                  'flex-1 flex flex-col items-center gap-0.5 py-2.5 text-xs font-medium transition-colors select-none relative',
                  activeTab === item.id
                    ? 'text-[#0AC18E]'
                    : 'text-zinc-400 dark:text-zinc-500 hover:text-zinc-600 dark:hover:text-zinc-400'
                )}
                aria-label={item.label}
              >
                <span className="text-lg leading-none">{item.icon}</span>
                <span>{item.label}</span>
                {/* Product count badge on Menu tab */}
                {item.id === 'menu-manage' && totalProducts > 0 && activeTab !== 'menu-manage' && (
                  <span className="absolute top-1.5 right-[20%] w-4 h-4 bg-zinc-300 dark:bg-zinc-600 text-zinc-600 dark:text-zinc-300 text-[9px] font-black rounded-full flex items-center justify-center">
                    {totalProducts > 99 ? '99+' : totalProducts}
                  </span>
                )}
                {activeTab === item.id && (
                  <span className="w-1 h-1 rounded-full bg-[#0AC18E]" />
                )}
              </button>
            ))}
          </div>
        </nav>
      )}
    </div>
  );
}
